/* Feb-2026 · Adults-Only super-shelf page (/bookstore/g/adults-only).
 *
 * Grid view of the child shelves — Dark Romance, Grey Romance, Adult
 * Fiction, and Erotica. Each child tile still fires its own
 * GenreContentWarningDialog on first click (per-shelf triggers) — the
 * AdultsOnlyGate handled the outer 18+ agreement one screen up.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldAlert, Flame } from 'lucide-react';
import { bookstoreApi } from '@/lib/bookstoreApi';
import { P, FONTS } from '@/components/landing/scriptorium/palette';
import { useSeoMeta } from '@/lib/useSeoMeta';   // Feb-2026 · route SEO
import GenreContentWarningDialog, {
  hasAcceptedContentWarning,
} from '@/components/bookstore/GenreContentWarningDialog';
import AdultsOnlyGate, { hasAcceptedAdultsOnly } from '@/components/bookstore/AdultsOnlyGate';

const CHILD_GRADIENTS = {
  'dark-romance': `linear-gradient(135deg,#3d0910,#0d0405)`,
  'grey-romance': `linear-gradient(135deg,#1c1a34,${P.oxblood})`,
  'adult-fiction': `linear-gradient(135deg,#2a2018,#120d08)`,
  erotica:         `linear-gradient(135deg,#4a0511,#1a0205)`,
};

export default function AdultsOnlyLanding() {
  useSeoMeta('/bookstore/g/adults-only', {
    title: 'Mature Audiences · Dark & Grey Romance, Adult Fiction & Erotica · Crown & Rose',
    description: 'The gated 18+ shelves — a large curated indie collection of Dark Romance (mafia, bratva, cartel, captive), Grey Romance (morally-grey anti-heroes), Adult Fiction, and Erotica, with per-book trigger warnings.',
    keywords: 'dark romance, mature romance, mafia romance, bratva romance, cartel romance, captive romance, morally grey romance, adult fiction, erotica, indie dark romance, 18+ romance',
    og_title: 'Mature Audiences · Crown & Rose',
    og_description: '18+ Dark & Grey Romance, Adult Fiction, and Erotica — mafia, bratva, cartel, obsessive, captive. Curated indie titles with per-book trigger warnings.',
  });
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [children, setChildren] = useState([]);
  const [gateOpen, setGateOpen] = useState(!hasAcceptedAdultsOnly());
  const [pendingChild, setPendingChild] = useState(null);

  useEffect(() => {
    bookstoreApi.catalog().then(setCatalog).catch(() => setCatalog({ genres: [] }));
  }, []);

  const parent = (catalog?.genres || []).find((g) => g.slug === 'adults-only');

  // iter-door-open-followup — `catalog.genres` deliberately drops every
  // row flagged `hide_from_main_shelves` (that's how the children stay
  // off the main carousel), so the parent's child rows can never be
  // found in that array. Fetch each child's full genre metadata
  // straight from its own detail endpoint instead — the same call the
  // genre page itself uses, so it isn't filtered by that flag.
  useEffect(() => {
    const slugs = parent?.children_slugs || ['dark-romance', 'grey-romance', 'adult-fiction', 'erotica'];
    Promise.allSettled(slugs.map((slug) => bookstoreApi.booksByGenre(slug)))
      .then((results) => {
        setChildren(
          results
            .filter((r) => r.status === 'fulfilled' && r.value?.genre)
            .map((r) => ({ ...r.value.genre, book_count: (r.value.books || []).length })),
        );
      });
  }, [parent?.children_slugs]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: P.paper, color: P.ink }}>
      <AdultsOnlyGate
        open={gateOpen}
        checklist={parent?.gate_checklist}
        onAccept={() => setGateOpen(false)}
        onRetreat={() => { setGateOpen(false); navigate('/bookstore'); }}
      />

      <header className="sticky top-0 z-30 border-b" style={{ borderColor: `${P.gold}66`, backgroundColor: `${P.paper}e6`, backdropFilter: 'blur(6px)' }}>
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-3">
          <Link to="/bookstore" data-testid="ao-nav-back" className="inline-flex items-center gap-1 text-[12px] hover:opacity-80" style={{ color: P.inkSoft }}>
            <ArrowLeft className="h-3 w-3" /> Back to shelves
          </Link>
          <div className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10.5px] uppercase tracking-[0.28em]" style={{ borderColor: P.oxblood, backgroundColor: `${P.oxblood}0d`, color: P.oxblood, fontFamily: FONTS.type }}>
            <ShieldAlert className="h-3 w-3" /> Mature Audiences Only · 18+
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-5xl px-5 py-10">
        <h1 className="text-4xl tracking-tight sm:text-5xl" style={{ fontFamily: FONTS.display, fontWeight: 500 }}>
          {parent?.name || 'Mature Audiences Only'}
        </h1>
        <p className="mt-2 max-w-2xl text-[13px] leading-relaxed" style={{ color: P.inkSoft }}>
          {parent?.manifesto || parent?.blurb ||
             'The 18+ shelves — Dark Romance, Grey Romance, Adult Fiction, and Erotica — behind a single mature-audiences gate. Each shelf still shows its own trigger warning the first time you open it.'}
        </p>

        <div className="mt-8 grid gap-5 sm:grid-cols-2">
          {children.map((g) => (
            <button
              key={g.slug}
              type="button"
              onClick={() => {
                // Per-shelf trigger warning still fires on first visit.
                if (g.content_warning && !hasAcceptedContentWarning(g.slug)) {
                  setPendingChild(g);
                } else {
                  navigate(`/bookstore/g/${g.slug}`);
                }
              }}
              data-testid={`ao-child-${g.slug}`}
              className="group relative aspect-[16/9] w-full overflow-hidden rounded-lg border shadow-panel-active transition hover:-translate-y-0.5 hover:shadow-xl"
              style={{ borderColor: `${P.gold}88`, backgroundImage: CHILD_GRADIENTS[g.slug] || 'linear-gradient(135deg,#222,#000)', color: '#fff' }}
            >
              <div className="absolute inset-0 flex flex-col justify-between p-5 text-left">
                <div className="inline-flex items-center gap-1.5 rounded-full border border-white/40 bg-black/40 px-2 py-0.5 text-[10px] uppercase tracking-[0.22em]">
                  <Flame className="h-3 w-3" /> Trigger warnings apply
                </div>
                <div>
                  <div className="text-2xl font-semibold tracking-tight" style={{ fontFamily: FONTS.display }}>
                    {g.name}
                  </div>
                  <div className="mt-1 line-clamp-2 max-w-md text-[12.5px] leading-snug opacity-90">
                    {g.blurb || ''}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-8 rounded-md border p-3 text-[11.5px] leading-relaxed" style={{ borderColor: `${P.oxblood}44`, backgroundColor: `${P.oxblood}08`, color: P.inkSoft }}>
          Every title inside Mature Audiences Only shows its trigger list on hover.
          Look for <strong>Extreme</strong> heat markers, and content chips such as
          <em> kidnapping</em>, <em>non-consent</em>, and <em>violence against protagonist</em>.
        </div>
      </section>

      <GenreContentWarningDialog
        genre={pendingChild}
        onAcceptOnce={(g) => { setPendingChild(null); navigate(`/bookstore/g/${g.slug}`); }}
        onRetreat={() => setPendingChild(null)}
      />
    </div>
  );
}
